import { ClickAwayListener, Grid, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Layout from '../../components/Layout/Layout.component';
import CancelEditingNewMessagePrompt from '../../components/Mailbox/CancelEditingNewMessagePrompt.component';
import { MailboxFolders } from '../../components/Mailbox/MailboxFolders.component';
import MessagePreview from '../../components/Mailbox/MessagePreview/MessagePreview.component';
import MessagesList from '../../components/Mailbox/MessagesList/MessagesList.component';
import { NewMessage } from '../../components/NewMessage/NewMessage.component';
import { SearchInput } from '../../components/SearchInput/SearchInput.component';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { Folder, foldersForHelpdesk, Priority } from '../../models/communication.types';
import { mailboxActions } from '../../reducers/mailboxReducer/mailboxSlice';
import { RootState } from '../../store';
import { useStyles } from '../styles';
import MessageCreate from './MessageCreate/MessageCreate.component';
import Breadcrumb from '../../components/Breadcrumbs.component';
import { Paths } from '../../utils/routerUrls';
import { getFolderAndSubFoldersCaptions } from '../../components/Mailbox/helpers';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Stack } from '@mui/material';

export const Mailbox: React.FC = () => {
  const classes = useStyles();
  const isSmallWidth = useMediaQuery('(max-width:1210px)');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useDocumentTitle('Skrzynka odbiorcza');
  const [urlQueryParams, setUrlQueryParams] = useState<{
    folder?: Folder;
    session?: string;
    priority?: Priority;
    isRead?: boolean;
  }>();
  const [openNewMessage, setOpenNewMessage] = useState<boolean>(false);
  const [messageType, setMessageType] = useState<'standard' | 'helpdesk'>('standard');
  const [isCancelPromptOpen, setIsCancelPromptOpen] = useState<boolean>(false);
  const [searchPhrase, setSearchPhrase] = useState<string>('');

  const isMenuMinimized = useSelector((state: RootState) => state.mailbox.isMenuMinimized);
  const activeFolder = useSelector((state: RootState) => state.mailbox.activeFolder);
  const activeMessageId = useSelector((state: RootState) => state.mailbox.activeMessageId);
  const replyMessageId = useSelector((state: RootState) => state.mailbox.replyMessageId);
  const forwardMessageId = useSelector((state: RootState) => state.mailbox.forwardMessageId);
  const isReplyOrForwardOpened = replyMessageId || forwardMessageId;

  const location = useLocation();

  const { folder, subFolder } = getFolderAndSubFoldersCaptions(activeFolder);

  useEffect(() => {
    const queryParams = location.search;
    const searchParams = new URLSearchParams(queryParams);
    if (queryParams.length > 0) {
      const folder: Folder | undefined = Number(searchParams.get('folder')) || undefined;
      const sessionName = decodeURIComponent(searchParams.get('session') || '');
      const session: string | undefined = sessionName !== '' ? sessionName : undefined;
      const priority: Priority | undefined = Number(searchParams.get('priority')) || undefined;
      const isRead: boolean | undefined = searchParams.get('isRead') === 'false' ? false : undefined;
      setUrlQueryParams({ folder, session, priority, isRead });
    }
  }, []);

  useEffect(() => {
    if (isReplyOrForwardOpened) {
      setOpenNewMessage(true);
      setMessageType(foldersForHelpdesk.includes(activeFolder) ? 'helpdesk' : 'standard');
    }
  }, [isReplyOrForwardOpened]);

  const handleOnClickAwayEditingMessage = () => {
    setIsCancelPromptOpen(true);
  };

  const closeReplyingAndForwarding = () => {
    dispatch(mailboxActions.closeReplyingForwarding());
    setIsCancelPromptOpen(false);
    setOpenNewMessage(false);
  };

  useEffect(() => {
    setSearchPhrase('');
  }, [activeFolder]);

  return (
    <>
      <Layout viewType={'fullwidthWithMenu'}>
        <div style={{ width: '100%' }}>
          <Grid container style={{ marginBottom: isSmallWidth ? '18px' : '12px' }}>
            <Grid item xs={7}>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <Grid item xs={3}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Typography variant="h1" className={classes.gridHeader} style={{ marginLeft: '10px' }}>
                      Skrzynka odbiorcza
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={9} style={{ position: 'relative', left: '-30px', display: 'inline', width: '100%' }}>
                  <Breadcrumb
                    listRoute={Paths.mailbox}
                    currentElement={`Skrzynka odbiorcza / ${folder} / ${subFolder}`}
                  />
                </Grid>
              </div>
            </Grid>
            <Grid item xs={5}>
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'row' }}>
                <div style={{ flexGrow: 2 }}>
                  <SearchInput
                    onSearch={setSearchPhrase}
                    searchInput={searchPhrase}
                    placeholder="Wyszukaj wiadomość"
                    inputStyle={{ width: '100%', fontSize: '14px' }}
                    activeStyle={{ border: '1px solid #2C2D3E4D', backgroundColor: 'white' }}
                    inactiveStyle={{ backgroundColor: 'white' }}
                    phraseMinLengthToStartSearching={3}
                    searchAfterClick={true}
                  />
                </div>
                <div>
                  <MessageCreate
                    onOpenNewMessage={(data) => {
                      setOpenNewMessage(data.value);
                      setMessageType(data.type);
                      dispatch(mailboxActions.deActivateMessage());
                    }}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={0}>
            <div
              style={{
                height: 'calc(100vh - 140px)',
                width: isMenuMinimized ? '50px' : '170px',
                padding: '20px 10px',
                boxSizing: 'border-box',
              }}
            >
              <MailboxFolders forcedFolder={urlQueryParams?.folder} isMenuMinimized={isMenuMinimized} />
            </div>
            <Grid container>
              <Grid item xs={6}>
                <MessagesList
                  forcedSession={urlQueryParams?.session}
                  forcedPriority={urlQueryParams?.priority}
                  forcedIsRead={urlQueryParams?.isRead}
                  searchPhrase={searchPhrase}
                  onSetFilters={() => setSearchPhrase('')}
                />
              </Grid>
              <Grid item xs={6}>
                <div
                  style={{
                    height: 'calc(100vh - 110px)',
                    boxSizing: 'border-box',
                    overflow: 'auto', //needed for displaying scrollbar when reply/forward displayed (please don't remove..... again ;) )
                  }}
                >
                  {openNewMessage && (
                    <div
                      className={classes.roundedWhiteContener}
                      style={{
                        height: isReplyOrForwardOpened ? 'calc(100vh - 265px)' : 'calc(100vh - 150px)',
                        margin: '0px 12px 0px 0px',
                        padding: '15px',
                        boxSizing: 'border-box',
                        position: 'relative',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                      }}
                    >
                      <ClickAwayListener
                        onClickAway={handleOnClickAwayEditingMessage}
                        mouseEvent={!isCancelPromptOpen ? 'onMouseDown' : false}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            height: '100%',
                          }}
                        >
                          <NewMessage
                            isModal={false}
                            messageType={messageType}
                            onCancel={closeReplyingAndForwarding}
                            onSend={closeReplyingAndForwarding}
                          />
                        </div>
                      </ClickAwayListener>
                    </div>
                  )}

                  {(activeMessageId || isReplyOrForwardOpened) && !openNewMessage && (
                    <div
                      className={classes.roundedWhiteContener}
                      style={{
                        height: 'calc(100vh - 150px)',
                        margin: '0 12px 12px 0',
                        padding: '0',
                        boxSizing: 'border-box',
                      }}
                    >
                      <MessagePreview />
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </Stack>
        </div>
      </Layout>
      {isCancelPromptOpen && (
        <CancelEditingNewMessagePrompt
          open={true}
          onConfirm={closeReplyingAndForwarding}
          onCancel={() => {
            setIsCancelPromptOpen(false);
          }}
        />
      )}
    </>
  );
};
